import { RiskGroupShortCodeEnum } from '../enums/risk-group-short-code.enum';
import { RiskLevelEnum } from '../enums/task.enums';

/**
 * Enum representing wallet owner types
 */
export enum WalletOwnerTypeEnum {
  System = 0,
  Merchant = 1,
}

/**
 * Enum representing wallet types
 */
export enum WalletTypeEnum {
  Pooled = 0,
  Dedicated = 1,
  OnetimeUse = 2,
  /** Per-(merchant, network) "Unsupported Deposit Funds" wallet: the sink for native coin (TRX, ETH). */
  UnsupportedFunds = 3,
}

/**
 * Enum representing system wallet types
 */
export enum SystemWalletTypeEnum {
  Normal = 0,
  Fees = 1,
  Staking = 2,
  Burning = 3,
  Funding = 4,
}

/**
 * Enum representing wallet status
 */
export enum WalletStatusEnum {
  Active = 0,
  Inactive = 1,
  Pending = 2,
}

export enum WalletLifecycleStateEnum {
  Serving = -1,
  Active = 0,
  Capped = 1,
  Closed = 2,
  Queued = 3,
}

/**
 * Enum representing whitelisted wallet status
 */
export enum WhitelistedWalletStatusEnum {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  Revoked = 3,
}

/**
 * Enum representing whitelisted wallet types
 */
export enum WhitelistedWalletType {
  SettlementPayout = 0,
  ClientPayout = 1,
  ServicePayout = 2,
}

/**
 * Enum representing withdrawal types
 */
export enum WithdrawalTypeEnum {
  Settlement = 0,
  InternalTransfer = 1,
  Payout = 2,
}

/**
 * Interface for wallet data transfer object
 * Secure version that excludes private key information
 */
export interface WalletDto {
  id: string;
  address: string;
  networkId: string;
  networkType: number;
  ownerType: WalletOwnerTypeEnum;
  merchantId?: string;
  merchantName?: string | null;
  walletType?: WalletTypeEnum;
  systemWalletType?: SystemWalletTypeEnum | null;
  publicKeyId?: string;
  publicKey: string | null;
  status: WalletStatusEnum;
  walletName: string;
  isExternalWallet: boolean;
  isSweepingEnabled: boolean;
  isActive: boolean;
  // EVM-compatible nonce (when supported by the blockchain)
  nonce?: number;
  // Risk assessment data
  riskLevel?: RiskLevelEnum;
  riskScore?: number;
  riskGroupShortCode?: RiskGroupShortCodeEnum | null;
  lifecycleState?: number;
  isServing?: boolean;
  lifecycleDetails?: WalletLifecycleDetails;
  canMoveNonSpendableFunds?: boolean;
  delegatedResources?: WalletDelegatedResources | null;
  maxCreditsAmountInBaseCurrency?: number;
  sequenceNumber?: number;

  // For one-time wallets
  withdrawalType?: string;

  // Wallet balances
  balance: WalletBalances;

  createdAt: Date;
  updatedAt: Date;
}

/**
 * Lifecycle details returned with a pooled wallet
 */
export interface WalletLifecycleDetails {
  storedMaxCap?: number;
  provisionedAt?: string;
  timeSinceProvisioned?: string;
  rotationProgress?: number;
  rotationTimeLeft?: string;
  rotatedAt?: string;
  timeSinceRotated?: string;
  baseCurrency?: string;
  baseCurrencyDisplayPrecision?: number;
}

/**
 * What the wallet's delegation pools hold and what withdrawals have claimed from them; transactionsUsed counts the
 * whole risk group's withdrawals today, as the daily allowance does
 */
export interface WalletDelegatedResources {
  delegatedEnergy: number;
  usedEnergy: number;
  delegatedBandwidth: number;
  usedBandwidth: number;
  transactionsAllocated: number;
  transactionsUsed: number;
}

export interface EligibleSweepWalletDto {
  id: string;
  address: string;
  walletName: string;
  defaultWallet: boolean;
  warningDetails: { title: string; description: string } | null;
}

/**
 * Interface for creating a merchant wallet
 */
export interface CreateMerchantWalletDto {
  addressFamily: number;
  merchantId: string;
  walletType: WalletTypeEnum;
  name?: string; // Optional name property for the wallet
}

/**
 * Interface for creating a systen wallet
 */
export interface CreateSystemWalletDto {
  addressFamily: number;
  walletType: WalletTypeEnum;
  systemWalletType?: SystemWalletTypeEnum;
  name?: string; // Optional name property for the wallet
}

/**
 * Interface for updating a merchant wallet
 */
export interface UpdateMerchantWalletDto {
  walletId: string;
  merchantId: string;
  walletName: string;
}

/**
 * Interface for updating a merchant wallet
 */
export interface UpdateSystemWalletDto {
  walletId: string;
  walletName: string;
}

/**
 * Interface for paged and sorted request
 */
export interface PagedAndSortedRequest {
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
}

/**
 * Interface for wallet filter parameters
 */
export interface WalletFilterParams extends PagedAndSortedRequest {
  walletStatuses?: number[] | undefined;
  merchantId?: string;
  status?: WalletStatusEnum[];
  walletType?: number[];
  systemWalletType?: number[];
  lifecycleState?: number[];
  networkType?: number[];
  filter?: string;
  blockchain?: number[];
  balance?: number[];
  riskLevel?: number[];
  startDate?: string;
  endDate?: string;
  merchantIds?: string[];
}

export interface WalletBalances {
  currencyBalances: CurrencyBalances[];
  baseCurrency: string;
  baseCurrencyDisplayPrecision: number;
  totalBalanceInBaseCurrency: number;
  availableBalanceInBaseCurrency: number;
}

export interface CurrencyBalances {
  currencyId: string;
  currencySymbol: string;
  currencyName?: string;
  riskDistribution?: RiskDistribution[];
  currencyDisplayPrecision: number;
  availableAmountInCurrency: number;
  availableBalanceInBaseCurrency?: number;
  totalAmountInCurrency: number;
  totalBalanceInBaseCurrency?: number;
}

export interface RiskDistribution {
  riskLevel: string;
  percentage: number;
  balance: number;
}
export interface CurrencyNetworkResponse {
  id: string;
  symbol: string;
  name: string;
  type: string;
  contractAddress: string | null;
  networkId: string;
  precision: number;
  createdAt: string;
  updatedAt: string;
}

export interface CurrencyOption {
  id: string;
  value: string;
  label: string;
  precision: number;
}
